// "Every N days / weeks / months" picker: Days / Weeks / Months switcher, an
// "Every [N]" stepper and a "Next: Oct 15, Oct 17, Oct 19, …" preview.
// Shared by the habit form (renderHabitForm.jsx, 2026-10-15) and the Planner
// add/edit panel (ItemPanel in CalendarExtras.jsx, 2026-10-16) so both look and
// behave the same. The rule itself is in src/utils/habitInterval.js.
import React from "react";
import { INTERVAL_UNITS, INTERVAL_RANGE, INTERVAL_DEFAULT_EVERY, intervalUnitOf, intervalEveryOf, isIntervalDueFrom } from "../../utils/habitInterval";
import { todayStr, fmt } from "../../utils/dateHelpers";

const UNIT_LABEL = { day: "Days", week: "Weeks", month: "Months" };

// value: { intervalUnit, intervalEvery }. anchor: the day the rhythm counts
// from ("" = today). onChange({ intervalUnit, intervalEvery }).
export function IntervalPicker({ value, anchor, onChange, btnStyle, background = "var(--c-bg-card)" }) {
  const unit = intervalUnitOf(value);
  const every = intervalEveryOf(value);
  const [lo, hi] = INTERVAL_RANGE[unit];
  const setEvery = (n) => onChange({ intervalUnit: unit, intervalEvery: Math.min(hi, Math.max(lo, n)) });
  // Preview: the next few due days from today, using the same rule the app does.
  const from0 = anchor || todayStr();
  const probe = { intervalUnit: unit, intervalEvery: every };
  const from = from0 > todayStr() ? from0 : todayStr();
  const next = [];
  for (let i = 0, d = new Date(from + "T00:00:00"); i < 800 && next.length < 3; i++, d.setDate(d.getDate() + 1)) {
    if (isIntervalDueFrom(probe, from0, fmt(d))) next.push(d.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
  }
  return (
    <div style={{ marginTop: 10, background, borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
      <div style={{ display: "flex", padding: 3, borderRadius: 12, background: "rgba(120,120,128,0.12)", marginBottom: 12 }}>
        {INTERVAL_UNITS.map(u => (
          <button key={u} type="button"
            onClick={() => onChange({ intervalUnit: u, intervalEvery: INTERVAL_DEFAULT_EVERY[u] })}
            style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: unit === u ? 700 : 500,
              background: unit === u ? "var(--c-bg-card)" : "transparent", color: unit === u ? "var(--c-primary)" : "#8a8a90",
              boxShadow: unit === u ? "0 1px 4px rgba(0,0,0,0.10)" : "none", transition: "background 0.2s, color 0.2s" }}>
            {UNIT_LABEL[u]}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#000000" }}>Every</span>
        <button type="button" onClick={() => setEvery(every - 1)} disabled={every <= lo}
          style={{ ...btnStyle, width: 30, height: 30, minHeight: 0, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, opacity: every <= lo ? 0.4 : 1 }}>−</button>
        <span style={{ fontSize: 20, fontWeight: 700, color: "var(--c-text-accent)", minWidth: 28, textAlign: "center" }}>{every}</span>
        <button type="button" onClick={() => setEvery(every + 1)} disabled={every >= hi}
          style={{ ...btnStyle, width: 30, height: 30, minHeight: 0, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)", color: "var(--c-text-accent)", borderColor: "var(--c-primary)", opacity: every >= hi ? 0.4 : 1 }}>+</button>
        <span style={{ fontSize: 13, color: "#000000" }}>{UNIT_LABEL[unit].toLowerCase()}</span>
      </div>
      {next.length > 0 && (
        <p style={{ fontSize: 11.5, color: "var(--c-text-accent)", fontWeight: 700, margin: "10px 0 0" }}>Next: {next.join(", ")}, …</p>
      )}
    </div>
  );
}
